import { useEffect, useState, useCallback } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import {
  LogOut, RefreshCw, Plus, Edit, Trash2, X, Save,
  Flame, Inbox, HandCoins, AlertCircle, BookOpen, Loader2, ExternalLink,
} from "lucide-react";
import { adminApi, tokenStorage } from "@/lib/api";
import Logo from "@/components/Logo";
import BrandBanner from "@/components/BrandBanner";

const EMPTY = {
  title: "", excerpt: "", body: "", category: "Insights",
  cover_url: "", author: "Sahyog Editorial", is_published: true,
};

const CATEGORIES = ["Insights", "Credit Score", "Career", "MSME", "Personal Finance", "Customer Story", "Announcements"];

export default function AdminBlogs() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const hasToken = Boolean(tokenStorage.get());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/blogs", { params: { only_published: false } });
      setItems(data || []);
    } catch (err) {
      if (err?.response?.status === 401) navigate("/admin", { replace: true });
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { if (hasToken) load(); }, [load, hasToken]);
  if (!hasToken) return <Navigate to="/admin" replace />;

  const open = (b) => { setForm(b ? { ...EMPTY, ...b } : EMPTY); setEditing(b || "new"); };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    if (form.title.length < 4) return alert("Title must be at least 4 characters.");
    if (form.excerpt.length < 10) return alert("Excerpt must be at least 10 characters.");
    if (form.body.length < 20) return alert("Body must be at least 20 characters.");
    setSaving(true);
    try {
      if (editing === "new") await adminApi.post("/blogs", form);
      else await adminApi.patch(`/blogs/${editing.id}`, form);
      await load(); close();
    } catch (err) {
      alert(err?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  const del = async (b) => {
    if (!window.confirm(`Delete blog "${b.title}"?`)) return;
    await adminApi.delete(`/blogs/${b.id}`); await load();
  };

  const logout = async () => {
    try { await adminApi.post("/auth/logout"); } catch (_e) { /* noop */ }
    tokenStorage.clear(); navigate("/admin", { replace: true });
  };

  return (
    <div data-testid="admin-blogs-page" className="min-h-screen bg-[#04102B] text-white">
      <header className="sticky top-0 z-30 bg-[#04102B]/90 backdrop-blur-md border-b border-[#D4AF37]/20">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-[140px] flex items-center justify-between gap-4">
          <button onClick={() => navigate("/")} className="flex items-center gap-4 text-left">
            <div className="leading-tight">
              <div className="font-display text-sm md:text-lg uppercase tracking-tight font-semibold whitespace-nowrap">SAHYOG FINANCE
              <br />
              <span className="text-[#D4AF37]">&amp; NOBLE INFINITY</span></div>
              <div className="font-display text-[#D4AF37] text-[10px] md:text-xs tracking-[0.18em] uppercase mt-1">Asset Management Pvt. Ltd. · Admin Console</div>
            </div>
          </button>
          <div className="flex items-center gap-3">
            <button onClick={load} disabled={loading} className="text-white/80 hover:text-[#F0D788] inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/10 text-base font-semibold">
              <RefreshCw size={20} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <a href="/admin/change-password" data-testid="admin-change-pw-link" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[#D4AF37]/40 hover:bg-[#D4AF37]/10 text-[#F0D788] text-base font-semibold">Change password</a>
            <button onClick={logout} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 hover:bg-white/15 text-sm border border-white/10"><LogOut size={14} /> Logout</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        <div className="flex flex-wrap items-center gap-3 mb-7">
          <Link data-testid="nav-tab-leads" to="/admin/leads" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#D4AF37]/40 text-sm"><Inbox size={14} /> Leads</Link>
          <Link data-testid="nav-tab-applications" to="/admin/applications" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#D4AF37]/40 text-sm"><HandCoins size={14} /> Applications</Link>
          <Link data-testid="nav-tab-grievances" to="/admin/grievances" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#D4AF37]/40 text-sm"><AlertCircle size={14} /> Grievances</Link>
          <Link data-testid="nav-tab-offers" to="/admin/offers" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.05] border border-white/10 hover:border-[#D4AF37]/40 text-sm"><Flame size={14} /> Hot Offers</Link>
          <span data-testid="nav-tab-blogs" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37] text-[#0A1F44] border border-[#D4AF37] text-sm font-semibold"><BookOpen size={14} /> Blogs ({items.length})</span>
          <button data-testid="blg-new" onClick={() => open(null)} className="ml-auto inline-flex items-center gap-2 px-5 py-2.5 rounded-full btn-gold text-sm font-semibold"><Plus size={14} /> New Post</button>
        </div>

        <section className="rounded-2xl border border-white/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.04] text-white/55 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="text-left p-4">Title</th>
                <th className="text-left p-4">Category</th>
                <th className="text-left p-4">Author</th>
                <th className="text-left p-4">Status</th>
                <th className="text-left p-4">Slug</th>
                <th className="text-right p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="p-10 text-center text-white/70">Loading…</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-white/70">No posts yet.</td></tr>}
              {!loading && items.map((b) => (
                <tr key={b.id} data-testid={`blg-row-${b.id}`} className="border-t border-white/5 hover:bg-white/[0.03]">
                  <td className="p-4 align-top">
                    <div className="font-display text-base text-white">{b.title}</div>
                    <div className="text-white/55 text-xs mt-0.5 line-clamp-2">{b.excerpt}</div>
                  </td>
                  <td className="p-4 align-top text-white/80">{b.category}</td>
                  <td className="p-4 align-top text-white/65 text-xs">{b.author}</td>
                  <td className="p-4 align-top">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border ${b.is_published ? "bg-emerald-400/15 text-emerald-200 border-emerald-400/30" : "bg-zinc-400/10 text-zinc-300 border-zinc-400/25"}`}>{b.is_published ? "Published" : "Draft"}</span>
                  </td>
                  <td className="p-4 align-top text-white/55 text-xs font-mono">{b.slug}</td>
                  <td className="p-4 align-top text-right whitespace-nowrap">
                    <a href={`/blogs/${b.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-emerald-300 hover:underline text-xs mr-3"><ExternalLink size={11} /> View</a>
                    <button data-testid={`blg-edit-${b.id}`} onClick={() => open(b)} className="inline-flex items-center gap-1 text-[#F0D788] hover:underline text-xs"><Edit size={12} /> Edit</button>
                    <button data-testid={`blg-del-${b.id}`} onClick={() => del(b)} className="ml-3 inline-flex items-center gap-1 text-red-300 hover:underline text-xs"><Trash2 size={12} /> Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </main>

      {editing && (
        <div data-testid="blg-modal" className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto">
          <div className="relative bg-[#0A1F44] rounded-3xl max-w-3xl w-full p-7 md:p-9 border border-[#D4AF37]/40 my-10">
            <button onClick={close} className="absolute top-5 right-5 text-white/55 hover:text-white"><X size={18} /></button>
            <h3 className="font-display text-2xl">{editing === "new" ? "New Blog Post" : "Edit Blog Post"}</h3>
            <div className="mt-6 space-y-4">
              <BInp label="Title *" k="title" form={form} setForm={setForm} />
              <div className="grid sm:grid-cols-3 gap-4">
                <BSel label="Category" k="category" form={form} setForm={setForm} opts={CATEGORIES} />
                <BInp label="Author" k="author" form={form} setForm={setForm} />
                <BInp label="Slug (optional)" k="slug" form={form} setForm={setForm} ph="auto-generated from title" />
              </div>
              <BInp label="Cover Image URL (optional)" k="cover_url" form={form} setForm={setForm} ph="https://…" />
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-white/55 mb-2">Excerpt * (10 – 400 chars)</label>
                <textarea data-testid="blg-field-excerpt" rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-white/[0.04] border border-[#D4AF37]/20 text-white outline-none focus:border-[#D4AF37] resize-none" />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-white/55 mb-2">Body * (Markdown / plain text)</label>
                <textarea data-testid="blg-field-body" rows={9} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-white/[0.04] border border-[#D4AF37]/20 text-white outline-none focus:border-[#D4AF37] resize-none font-mono text-xs" />
              </div>
              <label className="flex items-center gap-2.5 text-sm text-white/85 cursor-pointer">
                <input type="checkbox" data-testid="blg-field-is_published" checked={form.is_published} onChange={(e) => setForm({ ...form, is_published: e.target.checked })} className="w-4 h-4 accent-[#D4AF37]" /> Published (visible on /blogs)
              </label>
            </div>
            <div className="mt-7 flex justify-end gap-3">
              <button onClick={close} className="px-5 py-2.5 rounded-full border border-white/15 text-sm text-white/75 hover:text-white">Cancel</button>
              <button data-testid="blg-save" onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full btn-gold text-sm font-semibold disabled:opacity-60">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BInp({ label, k, form, setForm, ph }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-white/55 mb-2">{label}</label>
      <input
        data-testid={`blg-field-${k}`}
        value={form[k] || ""}
        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
        placeholder={ph || ""}
        className="w-full px-4 py-3 rounded-lg bg-white/[0.04] border border-[#D4AF37]/20 text-white outline-none focus:border-[#D4AF37]"
      />
    </div>
  );
}
function BSel({ label, k, form, setForm, opts }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-white/55 mb-2">{label}</label>
      <select
        data-testid={`blg-field-${k}`}
        value={form[k] || ""}
        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
        className="w-full px-4 py-3 rounded-lg bg-[#0A1F44] border border-[#D4AF37]/20 text-white outline-none focus:border-[#D4AF37]"
      >
        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
